import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FaHome, FaArrowRight } from "react-icons/fa";

import { widthAuthHOC } from "../../hoc";
import { Heading1 } from "../../components/Headings";

import {
  Form,
  Fieldset,
  PasswordInput,
  OTPInput,
  Button,
  MessageBox,
  MobileInput,
} from "../../components/FormElements";

import Loader from "../../components/Loader/Loader";

import {
  StyledButtonAsLink,
  StyledPara,
  StyledTextHighlight,
} from "../../components/styled";

import API from "../../api";

import {
  setAuthData,
  getSearchParams,
} from "../../utils";

import {
  RESPONSE_STATUSES,
  LOGIN_TYPES,
  OTP_VERIFICATION_TYPES,
} from "../../constants";


/* =========================================================
   STYLED COMPONENTS
========================================================= */

const AuthActions = styled.div`
display: flex;
align - items: center;
justify - content: space - between;
gap: 12px;
width: 100 %;
margin - top: 4px;

@media(max - width: 480px) {
  flex - direction: column;
  align - items: stretch;
}
`;


const SecondaryAction = styled.button`
display: inline - flex;
align - items: center;
justify - content: center;
gap: 6px;

min - height: 38px;
padding: 8px 14px;

border: 1px solid rgba(21, 0, 128, 0.15);
border - radius: 7px;

background: #fff;
color: #150080;

font - family: inherit;
font - size: 13px;
font - weight: 600;

cursor: pointer;
text - decoration: none;

transition: all 0.2s ease;

  &:hover {
  color: #150080;
  background: rgba(21, 0, 128, 0.045);
  border - color: rgba(21, 0, 128, 0.3);
  transform: translateY(-1px);
}

  &:disabled {
  opacity: 0.6;
  cursor: not - allowed;
  transform: none;
}
`;


const RegisterAction = styled(Link)`
display: inline - flex;
align - items: center;
justify - content: center;
gap: 7px;

min - height: 38px;
padding: 8px 15px;

border - radius: 7px;

background: rgba(21, 0, 128, 0.07);
color: #150080;

font - size: 13px;
font - weight: 600;
text - decoration: none;

transition: all 0.2s ease;

  svg {
  font - size: 11px;
}

  &:hover {
  background: #150080;
  color: #fff;
  transform: translateY(-1px);
  box - shadow: 0 5px 14px rgba(21, 0, 128, 0.15);
}
`;


const ForgotLink = styled(Link)`
display: inline - flex;
align - items: center;

color: #150080;
font - size: 13px;
font - weight: 600;
text - decoration: none;

transition: all 0.2s ease;

  &:hover {
  text - decoration: underline;
}
`;


const HomeAction = styled(Link)`
display: flex;
align - items: center;
justify - content: center;
gap: 8px;

width: fit - content;
margin: 22px auto 0;
padding: 9px 18px;

border: 1px solid rgba(21, 0, 128, 0.18);
border - radius: 7px;

background: rgba(21, 0, 128, 0.035);
color: #150080;

font - size: 13px;
font - weight: 600;
text - decoration: none;

transition: all 0.2s ease;

  svg {
  font - size: 12px;
}

  &:hover {
  background: #150080;
  border - color: #150080;
  color: #fff;
  transform: translateY(-1px);
  box - shadow: 0 5px 14px rgba(21, 0, 128, 0.16);
}
`;


const RegisterFooter = styled.div`
margin - top: 18px;

color: #687083;
font - size: 13px;
line - height: 1.5;
text - align: center;

  a {
  color: #150080;
  font - weight: 600;
  text - decoration: none;

    &:hover {
    text - decoration: underline;
  }
}
`;


/* =========================================================
   LOGIN COMPONENT
========================================================= */

const Login = () => {
  const [state, setState] = useState({
    isLoading: false,
    responseStatus: "",
    responseMessage: "",
    routeStage: "Login",
    loginType: LOGIN_TYPES.PASSWORD,
    mobileNumber: "",
    password: "",
    requestToken: "",
    oneTimePassword: "",
  });


  /* =========================================================
     LOGIN TYPE
  ========================================================= */

  const changeLoginType = () => {
    setState((prevState) => ({
      ...prevState,
      routeStage: "Login",
      password: "",
      requestToken: "",
      oneTimePassword: "",
      loginType:
        prevState.loginType === LOGIN_TYPES.PASSWORD
          ? LOGIN_TYPES.OTP
          : LOGIN_TYPES.PASSWORD,
    }));
  };


  /* =========================================================
     LOGIN / OTP SUBMIT
  ========================================================= */

  const onSubmit = async (e) => {
    e.preventDefault();

    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: "",
      responseMessage: "",
    }));

    try {
      const requestData =
        state.routeStage === "Login"
          ? state.loginType === LOGIN_TYPES.PASSWORD
            ? {
              loginType: state.loginType,
              mobileNumber: state.mobileNumber,
              password: state.password,
            }
            : {
              loginType: state.loginType,
              mobileNumber: state.mobileNumber,
            }
          : {
            requestType: OTP_VERIFICATION_TYPES.LOGIN,
            requestToken: state.requestToken,
            mobileNumber: state.mobileNumber,
            oneTimePassword: state.oneTimePassword,
          };

      const response = await API.post(
        state.routeStage === "Login"
          ? "/authentication/login"
          : "/authentication/verify-otp",
        requestData
      );

      const {
        status,
        message,
        data,
        token,
      } = response.data;


      if (status === RESPONSE_STATUSES.SUCCESS) {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.SUCCESS,
          responseMessage: message,
        }));

        if (
          state.loginType === LOGIN_TYPES.OTP &&
          state.routeStage === "Login"
        ) {
          setState((prevState) => ({
            ...prevState,
            requestToken: token,
            routeStage: "OTP",
          }));
        } else {
          setAuthData(token, data);
        }

      } else if (status === RESPONSE_STATUSES.TWOFA) {
        setState((prevState) => ({
          ...prevState,
          requestToken: token,
          routeStage: "OTP",
        }));

      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }

    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));

    } finally {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };


  /* =========================================================
     RESEND OTP
  ========================================================= */

  const resendOTP = async (e) => {
    e.preventDefault();

    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: "",
      responseMessage: "",
    }));

    try {
      const response = await API.post(
        "/authentication/resend-otp",
        {
          requestToken: state.requestToken,
          mobileNumber: state.mobileNumber,
        }
      );

      const {
        status,
        message,
        token,
      } = response.data;

      setState((prevState) => ({
        ...prevState,
        responseStatus: status,
        responseMessage: message,
        requestToken: token,
      }));

    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));

    } finally {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
    }
  };


  /* =========================================================
     URL ERROR MESSAGE
  ========================================================= */

  useEffect(() => {
    const errorMessage = getSearchParams("errorMessage");

    if (errorMessage) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: errorMessage,
      }));
    }
  }, []);


  /* =========================================================
     UI
  ========================================================= */

  return (
    <>
      <Loader isLoading={state.isLoading} />

      <Heading1 textAlign="center">
        {state.routeStage === "Login"
          ? "Login to your account"
          : "Verify & Proceed"}
      </Heading1>


      <Form
        action="#"
        method="POST"
        onSubmit={onSubmit}
      >

        {/* =====================================================
            LOGIN FIELDS
        ===================================================== */}

        {state.routeStage === "Login" && (
          <>
            <Fieldset>
              <MobileInput
                value={state.mobileNumber}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    mobileNumber: value,
                  }))
                }
                placeholder="Mobile Number"
                disabled={state.isLoading}
              />
            </Fieldset>


            {state.loginType === LOGIN_TYPES.PASSWORD && (
              <Fieldset>
                <PasswordInput
                  value={state.password}
                  onChange={(value) =>
                    setState((prevState) => ({
                      ...prevState,
                      password: value,
                    }))
                  }
                  placeholder="Password"
                  disabled={state.isLoading}
                />
              </Fieldset>
            )}
          </>
        )}


        {/* =====================================================
            OTP
        ===================================================== */}

        {state.routeStage === "OTP" && (
          <>
            <Fieldset>
              <StyledPara>
                Please enter your OTP sent to{" "}
                <StyledTextHighlight>
                  {state.mobileNumber}
                </StyledTextHighlight>
              </StyledPara>
            </Fieldset>


            <Fieldset>
              <OTPInput
                value={state.oneTimePassword}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    oneTimePassword: value,
                  }))
                }
                placeholder="OTP"
                disabled={state.isLoading}
              />
            </Fieldset>
          </>
        )}


        {/* =====================================================
            SECONDARY ACTIONS
        ===================================================== */}

        <Fieldset display="block">

          {state.routeStage === "Login" && (
            <AuthActions>

              {state.loginType === LOGIN_TYPES.PASSWORD ? (
                <ForgotLink
                  to="/forgot-password"
                  title="Forgot Password"
                >
                  Forgot Password?
                </ForgotLink>
              ) : (
                <span />
              )}

            </AuthActions>
          )}


          {state.routeStage === "OTP" && (
            <AuthActions>

              <SecondaryAction
                type="button"
                onClick={() =>
                  setState((prevState) => ({
                    ...prevState,
                    routeStage: "Login",
                    oneTimePassword: "",
                    requestToken: "",
                  }))
                }
                disabled={state.isLoading}
              >
                Back to Login
              </SecondaryAction>


              <SecondaryAction
                type="button"
                onClick={resendOTP}
                disabled={state.isLoading}
              >
                Resend OTP
              </SecondaryAction>

            </AuthActions>
          )}

        </Fieldset>


        {/* =====================================================
            RESPONSE MESSAGE
        ===================================================== */}

        {state.responseStatus && state.responseMessage && (
          <Fieldset>
            <MessageBox
              status={state.responseStatus}
              message={state.responseMessage}
            />
          </Fieldset>
        )}


        {/* =====================================================
            PRIMARY BUTTON
        ===================================================== */}

        <Fieldset>
          <Button disabled={state.isLoading}>
            {state.routeStage === "Login"
              ? state.loginType === LOGIN_TYPES.PASSWORD
                ? "Login"
                : "Send OTP"
              : "Verify & Proceed"}
          </Button>
        </Fieldset>

      </Form>


      {/* =======================================================
          REGISTER FOOTER
      ======================================================= */}

      {state.routeStage !== "OTP" && (
        <RegisterFooter>
          Don't have an account?{" "}
          <Link
            to="/register"
            title="Register"
          >
            Register
          </Link>
        </RegisterFooter>
      )}


      {/* =======================================================
          HOME
      ======================================================= */}

      <HomeAction
        to="/"
        title="Back to Home"
      >
        <FaHome />
        <span>Back to Home</span>
      </HomeAction>

    </>
  );
};


export default widthAuthHOC(Login);
